import { useQueryClient } from '@tanstack/vue-query';
import { useRouter } from 'vue-router';

import { projectKeys, projectQueryOptions } from '@/entities/project';
import { type Team, teamKeys } from '@/entities/team';

import { useTeamCookie } from './use-team-cookie';

export function useTeamSwitch() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { setTeamCookie, clearTeamCookie } = useTeamCookie();

  async function switchToTeam(nextTeam: Team) {
    queryClient.removeQueries({ queryKey: projectKeys.project() });

    queryClient.setQueryData(teamKeys.team(nextTeam.uuid), nextTeam);

    setTeamCookie(nextTeam.uuid);

    const project = await queryClient.ensureQueryData(projectQueryOptions());

    queryClient.setQueryData(projectKeys.project(project.uuid), project);

    await queryClient.invalidateQueries({ queryKey: projectKeys.projects() });

    clearCacheQueries();

    router.push({
      name: 'project',
      params: { project_uuid: project.uuid },
    });
  }

  async function switchToPersonalAccount() {
    queryClient.removeQueries({ queryKey: teamKeys.all });
    queryClient.removeQueries({ queryKey: projectKeys.project() });

    clearTeamCookie();

    const project = await queryClient.ensureQueryData(projectQueryOptions());

    queryClient.setQueryData(projectKeys.project(project.uuid), project);

    await queryClient.invalidateQueries({ queryKey: projectKeys.projects() });

    clearCacheQueries();

    router.push({
      name: 'project',
      params: { project_uuid: project.uuid },
    });
  }

  // Clear all queries except the default queries
  function clearCacheQueries() {
    const exceptQueryKey = ['project', 'profile', 'team', 'teams', 'notifications'];

    queryClient.removeQueries({
      predicate(query) {
        return !exceptQueryKey.some((key) => query.queryKey.includes(key));
      },
    });
  }

  return { switchToTeam, switchToPersonalAccount };
}
